// ── TournamentPanels.tsx ──────────────────────────────────────────────────────
import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Dropdown,
  Header,
  Icon,
  Image,
  Input,
  Label,
  List,
  Loader,
  Message,
  Modal,
  Segment,
  Table,
} from "semantic-ui-react";
import { Link } from "react-router-dom";
import { useStore } from "../../app/stores/store";
import { useTranslation } from "react-i18next";
import {
  TournamentDto,
  TournamentRegistrationDto,
} from "../../app/models/Tournament";

// ── ConfirmRemoveModal ────────────────────────────────────────────────────────
function ConfirmRemoveModal({
  open,
  onClose,
  onConfirm,
  header,
  message,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  header: string;
  message: string;
  loading: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Modal size="mini" open={open} onClose={onClose} dimmer="blurring">
      <Modal.Header style={{ backgroundColor: "#f8f8f9", borderBottom: "1px solid #d4d4d5" }}>
        {header}
      </Modal.Header>
      <Modal.Content>
        <p>{message}</p>
        {loading && (
          <div style={{ textAlign: "center", margin: "15px 0" }}>
            <Loader active inline size="small" />
          </div>
        )}
      </Modal.Content>
      <Modal.Actions style={{ backgroundColor: "#f9fafb", borderTop: "1px solid #d4d4d5" }}>
        <Button onClick={onClose} disabled={loading}>
          {t("tournament.confirm.no")}
        </Button>
        <Button
          negative
          icon="trash"
          labelPosition="right"
          content={t("tournament.confirm.yes")}
          onClick={onConfirm}
          loading={loading}
          disabled={loading}
        />
      </Modal.Actions>
    </Modal>
  );
}

// ── TournamentRegistrationsPanel ──────────────────────────────────────────────
export const TournamentRegistrationsPanel = observer(
  function TournamentRegistrationsPanel({ tournament }: { tournament: TournamentDto }) {
    const { tournamentStore, teamStore } = useStore();
    const { t } = useTranslation();
    const [confirmWithdrawId, setConfirmWithdrawId] = useState<string | null>(null);
    const [confirmRejectId, setConfirmRejectId] = useState<string | null>(null);
    const [seedEditing, setSeedEditing] = useState<Record<string, string>>({});
    const [groupEditing, setGroupEditing] = useState<Record<string, string>>({});

    const canAdmin = tournament.isCurrentUserOrganizer || tournament.isCurrentUserCoOrganizer;
    const registrations = tournament.registrations ?? [];
    const registered = registrations.filter((r) => r.status === "Registered");
    const pending = registrations.filter((r) => r.status === "Pending");
    const rest = registrations.filter((r) =>
      ["Rejected", "Withdrawn", "Disqualified"].includes(r.status),
    );

    const statusColor: Record<string, string> = {
      Registered: "green", Pending: "yellow", Rejected: "red",
      Withdrawn: "grey", Disqualified: "red",
    };

    const canRegister =
      tournament.status === "RegistrationOpen" &&
      !tournament.isCurrentUserParticipant &&
      !tournament.isCurrentUserOrganizer;

    const myEligibleTeams = teamStore.myTeams.filter(
      (t_) => t_.status === "Active" && t_.sportID === tournament.sportId && t_.isCurrentUserCaptain,
    );

    const [selectedTeamId, setSelectedTeamId] = useState("");

    const handleRegister = async () => {
      if (!selectedTeamId) return;
      await tournamentStore.registerTeam(tournament.id, selectedTeamId);
    };

    const SaveSeed = ({ reg }: { reg: TournamentRegistrationDto }) => {
      const val = seedEditing[reg.id] ?? String(reg.seed ?? "");
      return (
        <Input
          size="mini"
          type="number"
          min={1}
          value={val}
          onChange={(e) => setSeedEditing((prev) => ({ ...prev, [reg.id]: e.target.value }))}
          style={{ width: 60 }}
          action={{
            icon: tournamentStore.isSubmitting(`reg:seed:${reg.id}`) ? "spinner" : "save",
            size: "mini",
            color: "teal",
            loading: tournamentStore.isSubmitting(`reg:seed:${reg.id}`),
            disabled: tournamentStore.submitting,
            onClick: () => {
              tournamentStore.updateSeed(tournament.id, reg.id, Number(seedEditing[reg.id]));
              setSeedEditing((prev) => { const n = { ...prev }; delete n[reg.id]; return n; });
            },
          }}
        />
      );
    };

    const SaveGroup = ({ reg }: { reg: TournamentRegistrationDto }) => {
      const val = groupEditing[reg.id] ?? String(reg.groupNumber ?? "");
      return (
        <Input
          size="mini"
          type="number"
          min={1}
          value={val}
          onChange={(e) => setGroupEditing((prev) => ({ ...prev, [reg.id]: e.target.value }))}
          style={{ width: 55 }}
          placeholder="G#"
          action={{
            icon: "save",
            size: "mini",
            color: "blue",
            loading: tournamentStore.isSubmitting(`reg:group:${reg.id}`),
            disabled: tournamentStore.submitting,
            onClick: () => {
              const num = parseInt(groupEditing[reg.id]);
              if (!isNaN(num)) tournamentStore.assignGroup(tournament.id, reg.id, num);
              setGroupEditing((prev) => { const n = { ...prev }; delete n[reg.id]; return n; });
            },
          }}
        />
      );
    };

    return (
      <div>
        {/* Register CTA */}
        {canRegister && (
          <Segment color="green" style={{ marginBottom: 16 }}>
            <Header as="h5">
              <Icon name="shield" />
              {t("tournament.registrations.register")}
            </Header>
            {myEligibleTeams.length === 0 ? (
              <Message warning>
                <Icon name="exclamation triangle" />
                {t("tournament.registrations.noEligibleTeam")}
              </Message>
            ) : (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <Dropdown
                  selection
                  options={myEligibleTeams.map((t_) => ({
                    key: t_.id,
                    value: t_.id,
                    text: t_.name,
                    image: { avatar: true, src: t_.logoUrl || "/assets/team-placeholder.png" },
                  }))}
                  value={selectedTeamId}
                  onChange={(_, d) => setSelectedTeamId(d.value as string)}
                  placeholder={t("tournament.registrations.selectTeam")}
                />
                <Button
                  positive
                  size="small"
                  disabled={!selectedTeamId || tournamentStore.submitting}
                  loading={tournamentStore.isSubmitting(`reg:register:${selectedTeamId}`)}
                  onClick={handleRegister}
                >
                  <Icon name="check" />
                  {t("tournament.registrations.submitRegistration")}
                </Button>
              </div>
            )}
            <div style={{ fontSize: "0.78em", color: "#888", marginTop: 8 }}>
              <Icon name="info circle" />
              {tournament.registrationMode === "ManualReview"
                ? t("tournament.registrations.manualReviewNote")
                : t("tournament.registrations.autoAcceptNote")}
            </div>
          </Segment>
        )}

        {/* Pending approvals — admin only */}
        {canAdmin && pending.length > 0 && (
          <Segment color="yellow" style={{ marginBottom: 16 }}>
            <Header as="h5" color="yellow">
              <Icon name="clock" />
              {t("tournament.registrations.pendingTitle")} ({pending.length})
            </Header>
            <List divided>
              {pending.map((reg) => (
                <List.Item key={reg.id} style={{ padding: "10px 0" }}>
                  <Image avatar src={reg.teamLogoUrl || "/assets/team-placeholder.png"} />
                  <List.Content>
                    <List.Header as={Link} to={`/teams/${reg.teamId}`}>{reg.teamName}</List.Header>
                    <List.Description style={{ fontSize: "0.8em" }}>
                      <Icon name="users" />{reg.memberCount} {t("tournament.teams")}
                    </List.Description>
                  </List.Content>
                  <List.Content floated="right" style={{ display: "flex", gap: 6 }}>
                    <Button
                      positive size="mini"
                      loading={tournamentStore.isSubmitting(`reg:approve:${reg.id}`)}
                      disabled={tournamentStore.submitting}
                      onClick={() => tournamentStore.approveRegistration(tournament.id, reg.id)}
                    >
                      <Icon name="check" />{t("tournament.registrations.approve")}
                    </Button>
                    <Button
                      negative basic size="mini"
                      disabled={tournamentStore.submitting}
                      onClick={() => setConfirmRejectId(reg.id)}
                    >
                      <Icon name="close" />{t("tournament.registrations.reject")}
                    </Button>
                  </List.Content>
                </List.Item>
              ))}
            </List>
          </Segment>
        )}

        {/* Registered teams */}
        <Header as="h4">
          <Icon name="users" />
          {t("tournament.registrations.registeredTitle")} ({registered.length}/{tournament.maxTeams})
        </Header>

        {registered.length === 0 ? (
          <Message info>
            <Icon name="info circle" />{t("tournament.registrations.noTeams")}
          </Message>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table celled compact unstackable size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("tournament.registrations.team")}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="center">{t("tournament.registrations.members")}</Table.HeaderCell>
                  {canAdmin && <Table.HeaderCell textAlign="center">{t("tournament.registrations.seed")}</Table.HeaderCell>}
                  {tournament.format === "GroupStageKnockout" && canAdmin && (
                    <Table.HeaderCell textAlign="center">{t("tournament.registrations.group")}</Table.HeaderCell>
                  )}
                  {canAdmin && <Table.HeaderCell textAlign="center">{t("tournament.registrations.actions")}</Table.HeaderCell>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {registered.map((reg) => (
                  <Table.Row key={reg.id}>
                    <Table.Cell>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Image
                          src={reg.teamLogoUrl || "/assets/team-placeholder.png"}
                          avatar
                          style={{ width: 28, height: 28 }}
                        />
                        <Link to={`/teams/${reg.teamId}`}>{reg.teamName}</Link>
                      </div>
                    </Table.Cell>
                    <Table.Cell textAlign="center">{reg.memberCount}</Table.Cell>
                    {canAdmin && (
                      <Table.Cell textAlign="center"><SaveSeed reg={reg} /></Table.Cell>
                    )}
                    {tournament.format === "GroupStageKnockout" && canAdmin && (
                      <Table.Cell textAlign="center"><SaveGroup reg={reg} /></Table.Cell>
                    )}
                    {canAdmin && (
                      <Table.Cell textAlign="center">
                        {tournament.status === "RegistrationOpen" && (
                          <Button
                            size="mini" basic color="red"
                            disabled={tournamentStore.submitting}
                            onClick={() => setConfirmWithdrawId(reg.id)}
                          >
                            <Icon name="trash" />{t("tournament.registrations.remove")}
                          </Button>
                        )}
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}

        {/* Historical registrations */}
        {rest.length > 0 && (
          <details style={{ marginTop: 16 }}>
            <summary style={{ cursor: "pointer", color: "#999", fontSize: "0.88em" }}>
              {t("tournament.registrations.showHistory")} ({rest.length})
            </summary>
            <List divided relaxed style={{ marginTop: 8 }}>
              {rest.map((reg) => (
                <List.Item key={reg.id}>
                  <Image avatar src={reg.teamLogoUrl || "/assets/team-placeholder.png"} />
                  <List.Content>
                    <List.Header>{reg.teamName}</List.Header>
                  </List.Content>
                  <List.Content floated="right">
                    <Label size="tiny" color={statusColor[reg.status] as any}>
                      {t(`tournament.registrations.status.${reg.status.toLowerCase()}`)}
                    </Label>
                  </List.Content>
                </List.Item>
              ))}
            </List>
          </details>
        )}

        <ConfirmRemoveModal
          open={!!confirmWithdrawId}
          onClose={() => setConfirmWithdrawId(null)}
          onConfirm={() => {
            if (confirmWithdrawId)
              tournamentStore.withdrawTeam(tournament.id, confirmWithdrawId);
            setConfirmWithdrawId(null);
          }}
          header={t("tournament.confirm.removeTeam")}
          message={t("tournament.confirm.removeTeamMessage")}
          loading={confirmWithdrawId
            ? tournamentStore.isSubmitting(`reg:withdraw:${confirmWithdrawId}`)
            : false}
        />

        <ConfirmRemoveModal
          open={!!confirmRejectId}
          onClose={() => setConfirmRejectId(null)}
          onConfirm={() => {
            if (confirmRejectId)
              tournamentStore.rejectRegistration(tournament.id, confirmRejectId);
            setConfirmRejectId(null);
          }}
          header={t("tournament.confirm.rejectTeam")}
          message={t("tournament.confirm.rejectTeamMessage")}
          loading={confirmRejectId
            ? tournamentStore.isSubmitting(`reg:reject:${confirmRejectId}`)
            : false}
        />
      </div>
    );
  },
);

// ── TournamentRefereesPanel ───────────────────────────────────────────────────
export const TournamentRefereesPanel = observer(
  function TournamentRefereesPanel({ tournament }: { tournament: TournamentDto }) {
    const { tournamentStore, userStore } = useStore();
    const { t } = useTranslation();
    const [query, setQuery] = useState("");
    const [refereeToRemove, setRefereeToRemove] = useState<string | null>(null);
    const currentUserId = userStore.user?.userID;
    const canAdmin = tournament.isCurrentUserOrganizer || tournament.isCurrentUserCoOrganizer;
    const refs = tournamentStore.referees;

    React.useEffect(() => {
      tournamentStore.loadReferees(tournament.id);
    }, [tournament.id]);

    const statusColor: Record<string, string> = {
      Invited: "yellow", Pending: "orange", Confirmed: "green", Declined: "red", Removed: "grey",
    };

    const myPendingInvite = refs.find((r) => r.isCurrentUserInvite);

    const handleInviteSearch = (q: string) => {
      setQuery(q);
      if (q.length >= 2) tournamentStore.searchUsersToInvite(tournament.id, q);
      else tournamentStore.clearInvitableUsers();
    };

    return (
      <div>
        {/* Respond to invite */}
        {myPendingInvite && (
          <Message info>
            <Icon name="envelope" />
            {t("tournament.referees.inviteReceived")}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Button
                positive size="small"
                loading={tournamentStore.isSubmitting(`referee:respond:${tournament.id}:accept`)}
                disabled={tournamentStore.submitting}
                onClick={() => tournamentStore.respondRefereeInvite(tournament.id, true)}
              >
                <Icon name="check" />{t("tournament.referees.accept")}
              </Button>
              <Button
                negative basic size="small"
                loading={tournamentStore.isSubmitting(`referee:respond:${tournament.id}:decline`)}
                disabled={tournamentStore.submitting}
                onClick={() => tournamentStore.respondRefereeInvite(tournament.id, false)}
              >
                <Icon name="close" />{t("tournament.referees.decline")}
              </Button>
            </div>
          </Message>
        )}

        {/* Self-apply */}
        {tournament.refereesCanApply &&
          !refs.some(
            (r) => r.userId === currentUserId && (r.status === "Confirmed" || r.status === "Pending"),
          ) && (
            <Button
              primary size="small"
              style={{ marginBottom: 16 }}
              loading={tournamentStore.isSubmitting(`referee:apply:${tournament.id}`)}
              disabled={tournamentStore.submitting}
              onClick={() => tournamentStore.applyAsReferee(tournament.id)}
            >
              <Icon name="hand paper" />{t("tournament.referees.applyAsReferee")}
            </Button>
          )}

        {/* Admin: invite search */}
        {canAdmin && (
          <Segment secondary style={{ marginBottom: 16 }}>
            <Header as="h5">
              <Icon name="user plus" />{t("tournament.referees.inviteTitle")}
            </Header>
            <Input
              fluid
              icon="search"
              placeholder={t("tournament.referees.searchPlaceholder")}
              value={query}
              onChange={(e) => handleInviteSearch(e.target.value)}
            />
            {tournamentStore.invitableUsers.length > 0 && (
              <List divided style={{ marginTop: 8 }}>
                {tournamentStore.invitableUsers.map((u) => (
                  <List.Item key={u.userId} style={{ padding: "8px 0" }}>
                    <Image avatar src={u.imageUrl || "/assets/user.png"} />
                    <List.Content>
                      <List.Header>{u.fullName}</List.Header>
                    </List.Content>
                    <List.Content floated="right">
                      {u.isAlreadyReferee ? (
                        <Label size="mini" color="green">
                          {t("tournament.referees.alreadyReferee")}
                        </Label>
                      ) : (
                        <Button
                          size="mini" primary
                          loading={tournamentStore.isSubmitting(`referee:invite:${u.userId}`)}
                          disabled={tournamentStore.submitting}
                          onClick={() => {
                            tournamentStore.inviteReferee(tournament.id, u.userId);
                            setQuery("");
                            tournamentStore.clearInvitableUsers();
                          }}
                        >
                          <Icon name="send" />{t("tournament.referees.invite")}
                        </Button>
                      )}
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            )}
          </Segment>
        )}

        {/* Referee list */}
        {refs.length === 0 ? (
          <Message info>
            <Icon name="info circle" />{t("tournament.referees.noReferees")}
          </Message>
        ) : (
          <List divided relaxed>
            {refs.map((ref) => (
              <List.Item key={ref.id}>
                <Image avatar src={ref.imageUrl || "/assets/user.png"} />
                <List.Content>
                  <List.Header>
                    <Link to={`/profiles/${ref.userId}`}>{ref.fullName}</Link>
                  </List.Header>
                  <List.Description style={{ fontSize: "0.8em" }}>
                    {ref.confirmedAt
                      ? `${t("tournament.referees.confirmedAt")} ${new Date(ref.confirmedAt).toLocaleDateString()}`
                      : `${t("tournament.referees.invitedAt")} ${new Date(ref.invitedAt).toLocaleDateString()}`}
                  </List.Description>
                </List.Content>
                <List.Content floated="right" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <Label size="tiny" color={statusColor[ref.status] as any}>
                    {t(`tournament.referees.status.${ref.status.toLowerCase()}`)}
                  </Label>
                  {canAdmin && ref.status === "Pending" && (
                    <Button
                      size="mini" positive
                      loading={tournamentStore.isSubmitting(`referee:approve:${ref.id}`)}
                      disabled={tournamentStore.submitting}
                      onClick={() => tournamentStore.approveReferee(tournament.id, ref.id)}
                    >
                      <Icon name="check" />{t("tournament.referees.approve")}
                    </Button>
                  )}
                  {canAdmin && (ref.status === "Confirmed" || ref.status === "Invited") && (
                    <Button
                      size="mini" basic color="red"
                      disabled={tournamentStore.submitting}
                      onClick={() => setRefereeToRemove(ref.id)}
                    >
                      <Icon name="trash" />
                    </Button>
                  )}
                </List.Content>
              </List.Item>
            ))}
          </List>
        )}

        <ConfirmRemoveModal
          open={!!refereeToRemove}
          onClose={() => setRefereeToRemove(null)}
          onConfirm={() => {
            if (refereeToRemove)
              tournamentStore.removeReferee(tournament.id, refereeToRemove);
            setRefereeToRemove(null);
          }}
          header={t("tournament.confirm.removeReferee")}
          message={t("tournament.confirm.removeRefereeMessage")}
          loading={refereeToRemove
            ? tournamentStore.isSubmitting(`referee:remove:${refereeToRemove}`)
            : false}
        />
      </div>
    );
  },
);

// ── TournamentOrganizerPanel ──────────────────────────────────────────────────
export const TournamentOrganizerPanel = observer(
  function TournamentOrganizerPanel({ tournament }: { tournament: TournamentDto }) {
    const { tournamentStore } = useStore();
    const { t } = useTranslation();
    const [coOrgQuery, setCoOrgQuery] = useState("");
    const [coOrgToRemove, setCoOrgToRemove] = useState<string | null>(null);

    const handleCoOrgSearch = (q: string) => {
      setCoOrgQuery(q);
      if (q.length >= 2) tournamentStore.searchUsersToInvite(tournament.id, q);
      else tournamentStore.clearInvitableUsers();
    };

    return (
      <div>
        <Header as="h4" color="teal">
          <Icon name="settings" />{t("tournament.admin.title")}
        </Header>

        {/* Quick stats */}
        <Segment secondary style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: "0.9em" }}>
            <div>
              <div style={{ color: "#999", fontSize: "0.8em" }}>{t("tournament.admin.registered")}</div>
              <div style={{ fontWeight: 700, fontSize: "1.3em" }}>
                {tournament.registrations?.filter((r) => r.status === "Registered").length ?? 0} / {tournament.maxTeams}
              </div>
            </div>
            <div>
              <div style={{ color: "#999", fontSize: "0.8em" }}>{t("tournament.admin.pending")}</div>
              <div style={{ fontWeight: 700, fontSize: "1.3em", color: "#f2711c" }}>
                {tournament.registrations?.filter((r) => r.status === "Pending").length ?? 0}
              </div>
            </div>
          </div>
        </Segment>

        {/* Co-organizers */}
        <Segment style={{ marginBottom: 16 }}>
          <Header as="h5">
            <Icon name="users" />{t("tournament.admin.coOrganizers")}
          </Header>

          {tournament.coOrganizers?.length > 0 && (
            <List divided style={{ marginBottom: 10 }}>
              {tournament.coOrganizers.map((co) => (
                <List.Item key={co.userId}>
                  <Image avatar src={co.imageUrl || "/assets/user.png"} />
                  <List.Content>
                    <List.Header>
                      <Link to={`/profiles/${co.userId}`}>{co.fullName}</Link>
                    </List.Header>
                  </List.Content>
                  <List.Content floated="right">
                    <Button
                      size="mini" basic color="red"
                      disabled={tournamentStore.submitting}
                      onClick={() => setCoOrgToRemove(co.userId)}
                    >
                      <Icon name="trash" />
                    </Button>
                  </List.Content>
                </List.Item>
              ))}
            </List>
          )}

          {tournament.isCurrentUserOrganizer && (
            <>
              <Input
                fluid
                icon="search"
                size="small"
                placeholder={t("tournament.admin.searchCoOrg")}
                value={coOrgQuery}
                onChange={(e) => handleCoOrgSearch(e.target.value)}
              />
              {tournamentStore.invitableUsers.length > 0 && coOrgQuery.length >= 2 && (
                <List divided style={{ marginTop: 8 }}>
                  {tournamentStore.invitableUsers.map((u) => (
                    <List.Item key={u.userId} style={{ padding: "8px 0" }}>
                      <Image avatar src={u.imageUrl || "/assets/user.png"} />
                      <List.Content>
                        <List.Header>{u.fullName}</List.Header>
                      </List.Content>
                      <List.Content floated="right">
                        {u.isAlreadyCoOrganizer ? (
                          <Label size="mini" color="green">
                            {t("tournament.admin.alreadyCoOrg")}
                          </Label>
                        ) : (
                          <Button
                            size="mini" primary
                            loading={tournamentStore.isSubmitting(`coorg:add:${u.userId}`)}
                            disabled={tournamentStore.submitting}
                            onClick={() => {
                              tournamentStore.addCoOrganizer(tournament.id, u.userId);
                              setCoOrgQuery("");
                              tournamentStore.clearInvitableUsers();
                            }}
                          >
                            <Icon name="plus" />{t("tournament.admin.add")}
                          </Button>
                        )}
                      </List.Content>
                    </List.Item>
                  ))}
                </List>
              )}
            </>
          )}
        </Segment>

        <ConfirmRemoveModal
          open={!!coOrgToRemove}
          onClose={() => setCoOrgToRemove(null)}
          onConfirm={() => {
            if (coOrgToRemove)
              tournamentStore.removeCoOrganizer(tournament.id, coOrgToRemove);
            setCoOrgToRemove(null);
          }}
          header={t("tournament.confirm.removeCoOrganizer")}
          message={t("tournament.confirm.removeCoOrganizerMessage")}
          loading={coOrgToRemove
            ? tournamentStore.isSubmitting(`coorg:remove:${coOrgToRemove}`)
            : false}
        />
      </div>
    );
  },
);

export default TournamentRegistrationsPanel;